/**
 * Featured Collection Section — Tab Switching
 * Pure vanilla JS, no setTimeout/setInterval
 */
(function () {
  'use strict';

  /**
   * Click-and-drag horizontal scrolling for the product carousels.
   *
   * Touch and trackpad already scroll natively, so this only adds mouse
   * dragging. A movement threshold distinguishes a drag from a click, so
   * clicking a card still follows its link; once past the threshold the
   * track takes pointer capture and the click is swallowed on release.
   */
  function initDragScroll(section) {
    var tracks = section.querySelectorAll('.oddit-section-featured-collection__grid');
    if (!tracks.length) return;

    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.dragBound === 'true') return;
      track.dataset.dragBound = 'true';

      var isDown = false;
      var didDrag = false;
      var startX = 0;
      var lastX = 0;
      var THRESHOLD = 5;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        isDown = true;
        didDrag = false;
        startX = e.clientX;
        lastX = e.clientX;
      });

      track.addEventListener('pointermove', function (e) {
        /* Adopt an in-progress drag whose pointerdown we never saw — it can be
           swallowed by a child, or by another listener calling stopPropagation.
           buttons === 1 means the primary button is still held. */
        if (!isDown && e.pointerType === 'mouse' && e.buttons === 1) {
          isDown = true;
          didDrag = false;
          startX = e.clientX;
          lastX = e.clientX;
          return;
        }

        if (!isDown) return;
        var dx = e.clientX - startX;

        if (!didDrag && Math.abs(dx) < THRESHOLD) return;

        if (!didDrag) {
          didDrag = true;
          track.classList.add('is-dragging');
          if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
        }

        e.preventDefault();

        /* Move by the delta since the last event rather than to an absolute
           position derived from where the drag began. The infinite loop rewinds
           scrollLeft by one set width mid-gesture; an absolute
           `startScroll - dx` would recompute from a baseline captured before
           that rewind and snap the track straight back, which cancelled the
           loop for anyone dragging rather than trackpad-scrolling. */
        var delta = e.clientX - lastX;

        /* Dragging right scrolls back, and back past zero is the hard edge —
           the clones only extend the track forwards. Jumping ahead by exactly
           one set the moment the gesture would cross it lands on a
           pixel-identical frame, so the drag keeps running instead of sticking
           at the first card. normalise() folds the borrowed set back on
           release. */
        if (delta > 0 && track.scrollLeft - delta < 0) {
          var setWidth = setWidthOf(track);
          if (setWidth > 0) track.scrollLeft += setWidth;
        }

        track.scrollLeft -= delta;
        lastX = e.clientX;
      });

      function endDrag(e) {
        if (!isDown) return;
        isDown = false;
        if (!didDrag) return;
        track.classList.remove('is-dragging');

        /* The click guard below consumes the synthetic click that follows a
           drag. When a drag ends off the track no click ever arrives, so
           didDrag stayed true and ate the shopper's next real click instead —
           cards stopped being clickable. Clear it once the click that belongs
           to this gesture has had its turn. */
        setTimeout(function () {
          didDrag = false;
        }, 0);

        /* Releasing the mouse is the settle point for a drag. scrollend is not
           guaranteed after scrollLeft was driven by hand, so rewind here too —
           normalise() is idempotent, so a later scrollend costs nothing. */
        if (typeof track.odditNormaliseLoop === 'function') track.odditNormaliseLoop();
        if (track.releasePointerCapture && e.pointerId !== undefined) {
          try {
            track.releasePointerCapture(e.pointerId);
          } catch (err) {
            /* pointer already released */
          }
        }
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('pointerleave', endDrag);

      /* Swallow the click that follows a drag so it does not open a product. */
      track.addEventListener(
        'click',
        function (e) {
          if (!didDrag) return;
          e.preventDefault();
          e.stopPropagation();
          didDrag = false;
        },
        true
      );

      /* Native image drag would otherwise hijack the gesture. */
      track.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
    });
  }

  /**
   * Infinite loop for the product carousels.
   *
   * Same approach as the featured-reviews carousel: append a duplicate of the
   * card set, and once the viewport passes the end of the originals subtract
   * exactly one set-width from scrollLeft. At that moment the clones on screen
   * are pixel-identical to the originals we jump back to, so the rewind is
   * invisible — no long visible scroll back to the start.
   *
   * The track is a flex carousel at every breakpoint now, so this applies
   * throughout. The isCarousel() guard stays: clones only make sense on a
   * single non-wrapping row, and it keeps the loop from misbehaving if the
   * track is ever laid out as a grid again.
   */
  var CLONE_ATTR = 'data-fc-clone';

  function trackCards(track) {
    return Array.prototype.slice.call(
      track.querySelectorAll('.oddit-section-featured-collection__card')
    );
  }

  function isCarousel(track) {
    return window.getComputedStyle(track).display === 'flex';
  }

  function removeClones(track) {
    var clones = track.querySelectorAll('[' + CLONE_ATTR + ']');
    Array.prototype.forEach.call(clones, function (clone) {
      clone.parentNode.removeChild(clone);
    });
  }

  function addClones(track, originals) {
    var frag = document.createDocumentFragment();
    originals.forEach(function (card) {
      var clone = card.cloneNode(true);
      clone.setAttribute(CLONE_ATTR, 'true');
      clone.setAttribute('aria-hidden', 'true');
      /* Visual filler only — keep the duplicates out of the tab order. */
      var focusables = clone.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      Array.prototype.forEach.call(focusables, function (el) {
        el.setAttribute('tabindex', '-1');
      });
      frag.appendChild(clone);
    });
    track.appendChild(frag);
  }

  /* Distance from the first original to the first clone — one full set
     including gaps.

     Reading offsetLeft forces a synchronous layout, and this is consulted from
     the scroll handler, which fires continuously through a momentum scroll. The
     result is cached on the track and cleared whenever the set changes. */
  function setWidthOf(track) {
    if (track.odditSetWidth != null) return track.odditSetWidth;

    var cards = trackCards(track);
    var originalCount = cards.filter(function (c) {
      return !c.hasAttribute(CLONE_ATTR);
    }).length;
    if (!originalCount || cards.length <= originalCount) return 0;

    track.odditSetWidth = cards[originalCount].offsetLeft - cards[0].offsetLeft;
    return track.odditSetWidth;
  }

  function initInfiniteLoop(section) {
    var tracks = section.querySelectorAll('.oddit-section-featured-collection__grid');
    if (!tracks.length) return;

    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.loopBound === 'true') return;
      track.dataset.loopBound = 'true';

      /* Re-entrancy guard: normalise() writes scrollLeft, which fires another
         scroll event. Without this the handler recurses on every rewind. */
      var normalising = false;

      function normalise() {
        if (normalising) return;
        var width = setWidthOf(track);
        if (width <= 0) return;
        if (track.scrollLeft >= width) {
          normalising = true;
          track.scrollLeft = track.scrollLeft - width;
          normalising = false;
        }
      }

      function sync() {
        /* Rebuilt from scratch rather than patched: this only runs on init,
           resize and tab reveal, and starting clean keeps the measurement below
           honest. */
        track.odditSetWidth = null;
        removeClones(track);

        var originals = trackCards(track).filter(function (c) {
          return !c.hasAttribute(CLONE_ATTR);
        });

        if (!isCarousel(track) || originals.length <= 1) return;

        /* Nothing to loop through if the set already fits without scrolling. */
        if (track.scrollWidth - track.clientWidth <= 1) return;

        /* One set, so the set width can be measured. */
        addClones(track, originals);
        track.odditSetWidth = null;

        /* How many more are needed.

           Two constraints. The viewport must never show bare track: the rewind
           fires at scrollLeft === setWidth, where the viewport spans
           [setWidth, setWidth + clientWidth], so the clones must cover at least
           a viewport. And because the rewind waits for the scroll to settle,
           there must be enough track past that point to absorb a whole fling —
           otherwise the shopper reaches the hard end mid-throw and the carousel
           just stops, which is the dead end at the end of the run.

           Budgeting three viewports of runway covers a hard fling, and never
           fewer than two sets so there is always a spare set to travel into. */
        var setWidth = setWidthOf(track);
        var neededSets = setWidth > 0
          ? Math.max(2, Math.ceil((3 * track.clientWidth) / setWidth))
          : 2;

        for (var i = 1; i < neededSets; i++) addClones(track, originals);

        track.odditSetWidth = null;
      }

      track.odditNormaliseLoop = normalise;

      /* Exposed so tab switching can re-run it. A panel that is not the active
         one is display:none, which makes every layout metric on its track zero
         — the overflow check below cannot succeed until the panel is visible,
         so the inactive tab would never get its clones. */
      track.odditSyncLoop = sync;

      /* Every card image is lazy, including the clones cloneNode copied the
         attribute onto. That is right for the initial paint — most of the track
         is off screen — but it means the images only begin fetching once they
         are almost in view, so scrolling on, and especially coming back round
         the loop, showed empty cards for a beat.

         Once the shopper touches the carousel they have signalled they intend
         to travel along it, so fetch the rest then. Flipping loading to eager
         starts the request immediately; the clones share their originals' URLs,
         so those are cache hits. Runs once. */
      var warmed = false;

      function warmImages() {
        if (warmed) return;
        warmed = true;
        var lazy = track.querySelectorAll('img[loading="lazy"]');
        Array.prototype.forEach.call(lazy, function (img) {
          img.loading = 'eager';
        });
      }

      ['pointerdown', 'wheel', 'touchstart', 'scroll'].forEach(function (type) {
        track.addEventListener(type, warmImages, { passive: true, once: true });
      });

      sync();

      /* Rewind only once the scroll has stopped.

         Rewinding mid-motion rewrites scrollLeft by a full set width while the
         browser is still animating the fling, so the cards visibly slide across
         one another — and it cancels the momentum. Waiting for the scroll to
         settle makes the jump invisible: nothing is moving, and the frame we
         land on is pixel-identical to the one we left. The extra clone sets are
         what make this safe, since a single fling cannot exhaust the track
         before it settles. */
      if ('onscrollend' in window) {
        track.addEventListener('scrollend', normalise, { passive: true });
      } else {
        var settleTimer = null;
        track.addEventListener(
          'scroll',
          function () {
            if (settleTimer) clearTimeout(settleTimer);
            settleTimer = setTimeout(function () {
              settleTimer = null;
              normalise();
            }, 140);
          },
          { passive: true }
        );
      }

      var resizeRaf = null;
      window.addEventListener(
        'resize',
        function () {
          if (resizeRaf) return;
          resizeRaf = requestAnimationFrame(function () {
            resizeRaf = null;
            sync();
          });
        },
        { passive: true }
      );
    });
  }

  /**
   * Prev/next arrows.
   *
   * The track has always scrolled, but nothing said so — a mouse user saw a
   * cropped row of cards with no control on it, so the products past the fold
   * went unfound. The buttons ship hidden from Liquid and are revealed here
   * only once the track is measured as overflowing, which also means a page
   * whose JS never ran shows no dead controls.
   *
   * A click travels one page: as many whole cards as currently fit, measured
   * rather than assumed, since the visible count is CSS-driven and changes
   * across breakpoints.
   */
  function initArrows(section) {
    var carousels = section.querySelectorAll(
      '.oddit-section-featured-collection__carousel'
    );
    if (!carousels.length) return;

    Array.prototype.forEach.call(carousels, function (carousel) {
      var track = carousel.querySelector('.oddit-section-featured-collection__grid');
      var prev = carousel.querySelector('.oddit-section-featured-collection__arrow--prev');
      var next = carousel.querySelector('.oddit-section-featured-collection__arrow--next');
      if (!track || !prev || !next) return;

      function step() {
        var cards = trackCards(track);
        if (cards.length < 2) return track.clientWidth;
        var cardStep = cards[1].offsetLeft - cards[0].offsetLeft;
        if (cardStep <= 0) cardStep = cards[0].offsetWidth;
        if (cardStep <= 0) return track.clientWidth;

        var styles = window.getComputedStyle(track);
        var inner =
          track.clientWidth -
          (parseFloat(styles.paddingLeft) || 0) -
          (parseFloat(styles.paddingRight) || 0);

        /* Floor, not round: a card peeking past the edge is not a card you can
           read, and paging by it would scroll a partial card off screen and
           leave the row misaligned against the snap points. */
        var fit = Math.max(1, Math.floor(inner / cardStep));
        return fit * cardStep;
      }

      function scrollByPage(direction) {
        var distance = step();

        /* Going back from the very start would hit the hard edge — the clones
           only extend the track forwards. Jumping ahead by exactly one set
           first lands on a pixel-identical frame, so the scroll that follows
           reads as the row simply continuing backwards. */
        if (direction < 0 && track.scrollLeft < distance) {
          var setWidth = setWidthOf(track);
          if (setWidth > 0) track.scrollLeft += setWidth;
        }

        track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      }

      function update() {
        /* Nothing to page through if the whole set already fits. */
        var overflows = track.scrollWidth - track.clientWidth > 1;
        prev.hidden = !overflows;
        next.hidden = !overflows;
      }

      /* Exposed so a tab reveal can re-measure: an inactive panel is
         display:none, where every metric above reads zero. */
      track.odditUpdateArrows = update;

      if (carousel.dataset.arrowsBound !== 'true') {
        carousel.dataset.arrowsBound = 'true';
        prev.addEventListener('click', function () {
          scrollByPage(-1);
        });
        next.addEventListener('click', function () {
          scrollByPage(1);
        });

        var arrowResizeRaf = null;
        window.addEventListener(
          'resize',
          function () {
            if (arrowResizeRaf) return;
            arrowResizeRaf = requestAnimationFrame(function () {
              arrowResizeRaf = null;
              update();
            });
          },
          { passive: true }
        );

        /* A first measurement taken before the cards have been laid out reads
           no overflow and would leave the arrows hidden for good, since nothing
           else re-measures until a resize or a tab switch. Take it again once
           the page has finished loading. */
        if (document.readyState !== 'complete') {
          window.addEventListener(
            'load',
            function () {
              requestAnimationFrame(update);
            },
            { once: true }
          );
        }
      }

      update();
    });
  }

  function initSection(section) {
    if (!section) return;

    /* Before the tabs guard — a section with a single collection and no tabs
       still has a carousel that must be draggable. */
    initDragScroll(section);
    initInfiniteLoop(section);
    initArrows(section);

    var tabs = section.querySelectorAll('.oddit-section-featured-collection__tab');
    var panels = section.querySelectorAll('.oddit-section-featured-collection__panel');
    if (!tabs.length) return;

    function activateTab(targetTab) {
      var index = targetTab.getAttribute('data-tab-index');

      tabs.forEach(function (tab) {
        tab.classList.remove('oddit-section-featured-collection__tab--active');
        tab.setAttribute('aria-selected', 'false');
      });

      panels.forEach(function (panel) {
        panel.classList.remove('oddit-section-featured-collection__panel--active');
      });

      targetTab.classList.add('oddit-section-featured-collection__tab--active');
      targetTab.setAttribute('aria-selected', 'true');

      /* The traits band belongs to the tab, not to the section: a pouch
         carries 50mg of caffeine and a stick 200mg. Every tab's row is already
         in the page, so this only picks which one is shown. A section whose
         traits are all on "All tabs" still renders a row per tab, so there is
         always one to reveal. */
      var traitRows = section.querySelectorAll('[data-traits-tab]');
      Array.prototype.forEach.call(traitRows, function (row) {
        row.hidden = row.getAttribute('data-traits-tab') !== index;
      });

      var targetPanel = section.querySelector('[data-tab-panel="' + index + '"]');
      if (targetPanel) {
        targetPanel.classList.add('oddit-section-featured-collection__panel--active');

        /* The panel was display:none until this line, so its track measured
           zero and initInfiniteLoop could not tell whether it overflows. Now
           that it is laid out, let it decide again — otherwise every tab except
           the one active at load would never get its clones. */
        var revealed = targetPanel.querySelectorAll(
          '.oddit-section-featured-collection__grid'
        );
        Array.prototype.forEach.call(revealed, function (track) {
          if (typeof track.odditSyncLoop === 'function') track.odditSyncLoop();
          if (typeof track.odditUpdateArrows === 'function') track.odditUpdateArrows();
        });
      }
    }

    /* Panels other than the active one are display:none, so their lazy images
       are never fetched — switching tabs requested the whole set at once and
       they popped in together. Flipping them to eager starts the requests while
       the panel is still hidden, so the tab shows an already-decoded set.
       Hidden images still fetch when eager; only `lazy` is deferred. */
    function warmPanel(panel) {
      if (!panel || panel.dataset.odditWarmed === 'true') return;
      panel.dataset.odditWarmed = 'true';
      var lazy = panel.querySelectorAll('img[loading="lazy"]');
      Array.prototype.forEach.call(lazy, function (img) {
        img.loading = 'eager';
      });

      /* Cards in a HIDDEN panel never intersect, so the reveal observer never
         marks them visible and switching tabs ran the whole 0.85s fade, with its
         80ms-per-card stagger, all at once — a flash rather than an entrance.
         Pre-resolve those only.

         Never the active panel: it is the one the observer can still reach, and
         forcing it visible here skipped the section's own scroll-in animation
         entirely. */
      var isActive = panel.classList.contains(
        'oddit-section-featured-collection__panel--active'
      );
      if (!isActive) {
        var hidden = panel.querySelectorAll('.oddit-reveal:not(.is-visible)');
        Array.prototype.forEach.call(hidden, function (el) {
          el.classList.add('is-visible');
        });
      }
    }

    function panelForTab(tab) {
      /* Panels are keyed by data-tab-panel, matching data-tab-index on the tab —
         the same lookup activateTab uses. They carry no id, so an aria-controls
         lookup found nothing and warming silently did nothing. */
      var index = tab.getAttribute('data-tab-index');
      return index === null
        ? null
        : section.querySelector('[data-tab-panel="' + index + '"]');
    }

    /* Hovering or focusing a tab is the earliest reliable hint. */
    tabs.forEach(function (tab) {
      ['pointerenter', 'focus'].forEach(function (type) {
        tab.addEventListener(type, function () {
          warmPanel(panelForTab(tab));
        }, { passive: true, once: true });
      });
    });

    /* Backstop for a shopper who clicks straight through without hovering:
       warm every panel once the page has gone idle, so this never competes with
       the initial render. */
    var warmAllIdle = function () {
      tabs.forEach(function (tab) { warmPanel(panelForTab(tab)); });
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warmAllIdle, { timeout: 3000 });
    } else {
      setTimeout(warmAllIdle, 2000);
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        warmPanel(panelForTab(tab));
        activateTab(tab);
      });

      tab.addEventListener('keydown', function (e) {
        var currentIndex = Array.prototype.indexOf.call(tabs, tab);
        var nextIndex;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          nextIndex = (currentIndex + 1) % tabs.length;
          tabs[nextIndex].focus();
          activateTab(tabs[nextIndex]);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          activateTab(tabs[nextIndex]);
        }
      });
    });
  }

  // Initialize on DOM ready
  var section = document.querySelector('.oddit-section-featured-collection');
  initSection(section);

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    var newSection = target.querySelector('.oddit-section-featured-collection');
    if (!newSection) newSection = target.closest('.oddit-section-featured-collection');
    initSection(newSection);
  });
})();
